/**
 * WaterEffect — no render targets, zero flickering.
 *
 * Features:
 *  - Subdivided mesh with vertex-displaced sine waves (visible 3D swells)
 *  - 4-layer scrolling normal maps (THREE.js Water.js technique) for ripple detail
 *  - Schlick Fresnel
 *  - Fake sky reflection with horizon/zenith gradient + sun glow at correct angle
 *  - Worley (cellular) caustics — proper underwater light patches
 *  - Three specular lobes: broad scatter + mid + sharp sun disk + sparkle
 *  - Subsurface scattering approximation (cyan back-scatter near sun)
 *  - Wave-crest foam
 *  - Day / night cycle
 */

import {
    BufferAttribute,
    BufferGeometry,
    Mesh,
    ShaderMaterial,
    TextureLoader,
    Vector3,
    RepeatWrapping,
} from 'three';

// ─── Shaders ─────────────────────────────────────────────────────────────────

const vertexShader = /* glsl */`
uniform float time;

varying vec2 vWorldXY;
varying vec3 vWorldPos;
varying vec3 vViewDir;
varying float vWaveHeight;

// Three sine waves composited for gentle 3D swells
float waveHeight(vec2 p, float t) {
    float w1 = sin(p.x * 0.05  + t * 0.9)  * cos(p.y * 0.04  + t * 0.7);
    float w2 = sin(p.x * 0.03  - t * 0.6  + p.y * 0.035) * cos(p.y * 0.06 + t * 1.1);
    float w3 = sin((p.x + p.y) * 0.025     + t * 0.75);
    return (w1 * 0.45 + w2 * 0.35 + w3 * 0.2) * 0.30;
}

void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    float h = waveHeight(worldPos.xy, time);
    worldPos.z += h;
    vWaveHeight = h;
    vWorldXY    = worldPos.xy;
    vWorldPos   = worldPos.xyz;
    vViewDir    = cameraPosition - worldPos.xyz;
    gl_Position = projectionMatrix * viewMatrix * worldPos;
}
`;

const fragmentShader = /* glsl */`
uniform sampler2D normalMap;
uniform float     time;
uniform float     dayNightCycle;
uniform vec3      sunDirection;

varying vec2  vWorldXY;
varying vec3  vWorldPos;
varying vec3  vViewDir;
varying float vWaveHeight;

// ── 4-layer normal map (THREE.js Water.js technique) ─────────────────────────
vec3 waterNormal(vec2 uv) {
    vec2 u0 = uv / 103.0                + vec2( time / 17.0,   time / 29.0);
    vec2 u1 = uv / 107.0                - vec2( time / 19.0,   time / 31.0);
    vec2 u2 = uv / vec2(8907.0, 9803.0) + vec2( time / 101.0,  time / 97.0);
    vec2 u3 = uv / vec2(1091.0, 1027.0) - vec2( time / 109.0,  time / 113.0);
    vec4 n = (texture2D(normalMap, u0) +
              texture2D(normalMap, u1) +
              texture2D(normalMap, u2) +
              texture2D(normalMap, u3)) * 0.5 - 1.0;
    return normalize(n.xyz * vec3(1.5, 1.5, 1.0));
}

// ── Worley (cellular) noise for realistic caustics ────────────────────────────
vec2 hash2(vec2 p) {
    p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
    return fract(sin(p) * 43758.5453);
}

float worley(vec2 p, float t) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    float md = 1.0;
    for (int x = -1; x <= 1; x++) {
        for (int y = -1; y <= 1; y++) {
            vec2 cell = vec2(float(x), float(y));
            vec2 rnd  = hash2(i + cell);
            vec2 pt   = cell + 0.5 + 0.45 * sin(t * 0.75 + 6.2831 * rnd);
            md = min(md, length(pt - f));
        }
    }
    return md;
}

float caustics(vec2 uv, float t) {
    float c = pow(1.0 - min(worley(uv * 4.0, t), worley(uv * 7.0, t * 0.65 + 1.3)), 4.0);
    return clamp(c * 2.2, 0.0, 1.0);
}

// ── Fake sky: gradient + sun glow ─────────────────────────────────────────────
vec3 fakeSky(vec3 dir, vec3 sunDir, float dayFactor) {
    float elev = clamp(dir.z, 0.0, 1.0);

    // Day sky gradient
    vec3 daySky = mix(vec3(0.72, 0.88, 1.00), vec3(0.08, 0.28, 0.78), pow(elev, 0.7));

    // Sun glow at the sun's reflected angle (two lobes: halo + disk)
    float sunDot = max(0.0, dot(normalize(dir), normalize(sunDir)));
    daySky += vec3(1.00, 0.85, 0.50) * pow(sunDot, 12.0) * 0.90;
    daySky += vec3(1.00, 0.92, 0.70) * pow(sunDot,  3.0) * 0.18;

    // Horizon haze
    daySky = mix(daySky, vec3(0.85, 0.92, 1.0), pow(1.0 - elev, 6.0) * 0.4);

    vec3 nightSky = mix(vec3(0.02, 0.03, 0.09), vec3(0.00, 0.01, 0.04), elev);
    return mix(nightSky, daySky, dayFactor);
}

void main() {
    vec3 viewDir = normalize(vViewDir);
    vec3 normal  = waterNormal(vWorldXY);
    vec3 sunDir  = normalize(sunDirection);

    // ── Schlick Fresnel ───────────────────────────────────────────────────────
    float NdotV   = max(dot(normal, viewDir), 0.0);
    float fresnel = 0.04 + 0.96 * pow(1.0 - NdotV, 5.0);

    float dayFactor = 1.0 - dayNightCycle;

    // ── Fake sky reflection ───────────────────────────────────────────────────
    vec3 reflDir   = reflect(-viewDir, normal);
    vec3 skyColor  = fakeSky(normalize(reflDir), sunDir, dayFactor);

    // ── Water body colour ─────────────────────────────────────────────────────
    vec3 deepWater    = vec3(0.01, 0.05, 0.13);
    vec3 shallowWater = vec3(0.03, 0.20, 0.30);
    vec3 waterBody    = mix(deepWater, shallowWater, NdotV * 0.6 + 0.1);
    waterBody         = mix(waterBody * 0.25, waterBody, dayFactor);

    vec3 color = mix(waterBody, skyColor, fresnel * 0.90);

    // ── Specular: three lobes ─────────────────────────────────────────────────
    vec3 halfVec = normalize(sunDir + viewDir);
    float NdotH  = max(dot(normal, halfVec), 0.0);

    float specBroad  = pow(NdotH,  40.0) * 0.40;   // wide scatter
    float specMid    = pow(NdotH, 200.0) * 1.20;   // mid highlight
    float specSharp  = pow(NdotH, 900.0) * 4.00;   // sharp sun disk

    // Sparkle: higher-frequency normal for point glints
    vec3  sparkleN   = waterNormal(vWorldXY * 0.25);
    float sparkle    = pow(max(dot(sparkleN, halfVec), 0.0), 2000.0) * 7.0;

    vec3 specTint = mix(vec3(0.25, 0.30, 0.50), vec3(1.0, 0.96, 0.82), dayFactor);
    color += specTint * (specBroad + specMid + specSharp + sparkle) * dayFactor;

    // ── Subsurface back-scatter (cyan tint near sun direction) ────────────────
    float sss = pow(max(dot(sunDir, viewDir), 0.0), 4.0);
    color += vec3(0.00, 0.28, 0.45) * sss * 0.35 * dayFactor;

    // ── Worley caustics (in less-reflective, direct-view areas) ──────────────
    float caus = caustics(vWorldXY * 0.012, time * 0.4);
    color += vec3(0.02, 0.14, 0.22) * caus * (1.0 - fresnel) * dayFactor;

    // ── Foam: wave-crest peaks + vertex swell peaks ───────────────────────────
    vec4 rawN = (texture2D(normalMap, vWorldXY / 103.0 + vec2(time/17.0, time/29.0)) +
                 texture2D(normalMap, vWorldXY / 107.0 - vec2(time/19.0, time/31.0))) * 0.5 - 1.0;
    float normalFoam = max(rawN.z - 0.42, 0.0) * 3.0;
    float swellFoam  = max(vWaveHeight - 0.12, 0.0) * 4.0;
    float foam = clamp(normalFoam + swellFoam, 0.0, 1.0);
    color = mix(color, vec3(0.80, 0.92, 1.0), foam * 0.30 * dayFactor);

    float alpha = mix(0.72, 0.95, fresnel);
    gl_FragColor = vec4(color, alpha);
}
`;

// ─── WaterEffect class ───────────────────────────────────────────────────────

export interface WaterPlaneInfo {
    /** XY footprint in WorldChunk local space (THREE.js units after ZZ_SCALE_IN). */
    minX: number; maxX: number;
    minY: number; maxY: number;
    /** Water surface height — charZ must be below this to be underwater. */
    surfaceZ: number;
}

export class WaterEffect extends Mesh {

    static _instances: WaterEffect[] = [];
    static waterPlanes: WaterPlaneInfo[] = [];
    static waveSpeed: number = 0.5;

    static setWaveSpeed(v: number): void { WaterEffect.waveSpeed = v; }

    constructor(geometry: BufferGeometry, normalMap: any) {
        super(geometry, new ShaderMaterial({
            vertexShader,
            fragmentShader,
            transparent: true,
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -2,
            polygonOffsetUnits: -2,
            uniforms: {
                normalMap:     { value: normalMap },
                time:          { value: 0.0 },
                dayNightCycle: { value: 0.0 },
                sunDirection:  { value: new Vector3(0.5, 0.3, 1.0).normalize() },
            },
        }));
    }

    static update(delta: number): void {
        for (const w of WaterEffect._instances) {
            (w.material as ShaderMaterial).uniforms['time'].value += delta * WaterEffect.waveSpeed;
        }
    }

    static setDayNight(value: number): void {
        for (const w of WaterEffect._instances) {
            (w.material as ShaderMaterial).uniforms['dayNightCycle'].value = value;
        }
    }

    static reset(): void {
        WaterEffect._instances = [];
    }

    static createForPlane(
        startX: number, startY: number, startZ: number,
        endX:   number, endY:   number
    ): WaterEffect {
        // Subdivided grid so vertex displacement produces visible 3D waves.
        const DIVS = 32;
        const cols = DIVS + 1;
        const rows = DIVS + 1;
        const verts   = new Float32Array(cols * rows * 3);
        const uvs     = new Float32Array(cols * rows * 2);
        const indices = new Uint32Array(DIVS * DIVS * 6);

        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const x = startX + (c / DIVS) * (endX - startX);
                const y = startY + (r / DIVS) * (endY - startY);
                const i = r * cols + c;
                verts[i * 3]     = x;
                verts[i * 3 + 1] = y;
                verts[i * 3 + 2] = startZ;
                uvs[i * 2]     = x / 16;
                uvs[i * 2 + 1] = y / 16;
            }
        }

        let idx = 0;
        for (let r = 0; r < DIVS; r++) {
            for (let c = 0; c < DIVS; c++) {
                const a = r * cols + c;
                const b = r * cols + c + 1;
                const d = (r + 1) * cols + c;
                const e = (r + 1) * cols + c + 1;
                indices[idx++] = a; indices[idx++] = b; indices[idx++] = d;
                indices[idx++] = b; indices[idx++] = e; indices[idx++] = d;
            }
        }

        const geo = new BufferGeometry();
        geo.setAttribute('position', new BufferAttribute(verts,   3));
        geo.setAttribute('uv',       new BufferAttribute(uvs,     2));
        geo.setIndex(new BufferAttribute(indices, 1));
        geo.computeBoundingSphere();
        geo.computeBoundingBox();

        const normalMap = new TextureLoader().load('/images/waternormals.jpg');
        normalMap.wrapS = RepeatWrapping;
        normalMap.wrapT = RepeatWrapping;

        const water = new WaterEffect(geo, normalMap);
        WaterEffect._instances.push(water);
        WaterEffect.waterPlanes.push({
            minX: Math.min(startX, endX), maxX: Math.max(startX, endX),
            minY: Math.min(startY, endY), maxY: Math.max(startY, endY),
            surfaceZ: startZ,
        });
        return water;
    }
}
